// Site-level welcome email settings. Singleton row in `site_settings`.
// Subject and HTML body are editable; the plain-text fallback is
// auto-derived at send time. The right-hand iframe shows a live
// preview with sample merge-token values substituted in.
//
// Per-signup-page customization is handled separately via the
// `welcome_top_message_html` field on signup_pages — that slots
// into this template at the {{{top_message_html}}} placeholder.
//
// Per-provider customization is handled by the
// {{{provider_instructions_html}}} placeholder, sourced from
// email_providers rows. The "fallback provider" picker on this
// page chooses which provider's instructions are used when the
// recipient is on an unknown domain.

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { siteSettingsApi, emailProvidersApi, ApiError } from '../lib/api';
import type {
  SiteSettingsWelcomeEmail,
  EmailProviderListItem,
} from '@shared/types';

const SAMPLE_VARS = {
  first_name: 'Bob',
  last_name: 'Smith',
  email: 'bob@example.com',
  unsubscribe_url: 'https://penobscotmorning.com/unsubscribe?token=preview',
};

const SAMPLE_TOP_MESSAGE = '';
const SAMPLE_PROVIDER_INSTRUCTIONS = `
<p style="font-family:'Source Sans 3',Helvetica,Arial,sans-serif; margin:0 0 12px 0; font-size:16px; line-height:1.55; color:#1A1F2E;">
&#128073; <strong>Sample provider</strong>: per-recipient instructions render here based on the subscriber's email domain.</p>`;

const TOP_MESSAGE_SLOT = '{{{top_message_html}}}';
const PROVIDER_INSTRUCTIONS_SLOT = '{{{provider_instructions_html}}}';
const TOKEN_RE = /\{\{\s*([a-z_][a-z0-9_]*)\s*(?:\|\s*([^}]*?)\s*)?\}\}/gi;

function escHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!),
  );
}

// Mirrors src/worker/scheduled_emails/render.ts so the preview
// matches what the cron processor will actually send.
function renderPreview(html: string, escape: boolean): string {
  const withTopMessage = html.split(TOP_MESSAGE_SLOT).join(SAMPLE_TOP_MESSAGE);
  const withProvider = withTopMessage
    .split(PROVIDER_INSTRUCTIONS_SLOT)
    .join(SAMPLE_PROVIDER_INSTRUCTIONS);
  return withProvider.replace(TOKEN_RE, (_m, name: string, fallback?: string) => {
    const key = name.toLowerCase() as keyof typeof SAMPLE_VARS;
    const raw = SAMPLE_VARS[key];
    const value = raw == null || raw === '' ? fallback ?? '' : raw;
    return escape ? escHtml(value) : value;
  });
}

export default function WelcomeEmailPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [okMessage, setOkMessage] = useState<string | null>(null);
  const [settings, setSettings] = useState<SiteSettingsWelcomeEmail | null>(null);
  const [subject, setSubject] = useState('');
  const [html, setHtml] = useState('');
  const [fallbackId, setFallbackId] = useState<string | null>(null);
  const [providers, setProviders] = useState<EmailProviderListItem[]>([]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      siteSettingsApi.getWelcomeEmail(),
      emailProvidersApi.list(),
    ])
      .then(([{ settings }, { providers }]) => {
        if (!alive) return;
        setSettings(settings);
        setSubject(settings.subject);
        setHtml(settings.html);
        setFallbackId(settings.default_fallback_provider_id);
        setProviders(providers.filter((p) => p.active));
      })
      .catch((e) => {
        if (!alive) return;
        setErr(e instanceof Error ? e.message : 'Failed to load');
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!settings) return;
    setErr(null);
    setOkMessage(null);
    setSaving(true);
    try {
      const res = await siteSettingsApi.updateWelcomeEmail({
        subject,
        html,
        default_fallback_provider_id: fallbackId,
        expected_updated_at: settings.updated_at,
      });
      setSettings({
        ...settings,
        subject,
        html,
        default_fallback_provider_id: fallbackId,
        updated_at: res.updated_at,
      });
      setOkMessage('Saved.');
    } catch (e) {
      if (e instanceof ApiError && e.code === 'conflict') {
        setErr(
          'Someone else edited these settings. Reload the page to see the latest version, then re-apply your changes.',
        );
      } else {
        setErr(e instanceof Error ? e.message : 'Failed to save');
      }
    } finally {
      setSaving(false);
    }
  }

  const previewSrcDoc = useMemo(() => renderPreview(html, true), [html]);
  const subjectPreview = useMemo(() => renderPreview(subject, false), [subject]);

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <Mail size={18} className="text-slate-600" />
        <h2 className="text-lg font-medium text-slate-900">Welcome email</h2>
      </div>
      <p className="text-sm text-slate-500 mb-6 max-w-3xl">
        The HTML below is sent to every new subscriber about a minute after
        they sign up — long enough for the second-step name input to
        land, so <code>{'{{first_name|friend}}'}</code> can be filled
        in. Per-signup-page customization is a single "top message"
        HTML fragment that slots in at the <code>{'{{{top_message_html}}}'}</code>{' '}
        placeholder. Per-provider safelist instructions slot in at
        <code className="ml-1">{'{{{provider_instructions_html}}}'}</code>{' '}
        — managed in{' '}
        <Link to="/settings/email-providers" className="text-blue-700 underline">
          Email Providers
        </Link>
        . Other supported tokens:
        <code className="ml-1">{'{{first_name}}'}</code>,
        <code className="ml-1">{'{{last_name}}'}</code>,
        <code className="ml-1">{'{{email}}'}</code>,
        <code className="ml-1">{'{{unsubscribe_url}}'}</code>.
      </p>

      {loading ? (
        <p className="text-sm text-slate-400">Loading…</p>
      ) : (
        <form onSubmit={save} className="space-y-4">
          {err && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
              {err}
            </div>
          )}
          {okMessage && (
            <div className="text-sm text-green-800 bg-green-50 border border-green-200 rounded px-3 py-2">
              {okMessage}
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">
              Subject
              <span className="ml-2 font-normal text-slate-400">
                Supports merge tokens. Preview:{' '}
                <span className="text-slate-600 italic">{subjectPreview}</span>
              </span>
            </label>
            <input
              type="text"
              required
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="input"
              placeholder="Welcome to Penobscot Morning, {{first_name|friend}}!"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">
              Fallback provider for unknown domains
              <span className="ml-2 font-normal text-slate-400">
                Their <code className="text-[11px] bg-slate-100 px-1 rounded">instructions_html</code> renders at the placeholder above when the recipient's domain isn't in the providers list.
              </span>
            </label>
            <select
              value={fallbackId ?? ''}
              onChange={(e) => setFallbackId(e.target.value === '' ? null : e.target.value)}
              className="input"
            >
              <option value="">— None (skip the per-provider block) —</option>
              {providers.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {p.has_instructions ? '' : ' (no instructions configured)'}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                HTML body
                <span className="ml-2 font-normal text-slate-400">
                  Edit on the left, see preview on the right.
                </span>
              </label>
              <textarea
                rows={28}
                required
                value={html}
                onChange={(e) => setHtml(e.target.value)}
                className="input font-mono text-xs leading-relaxed"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                Preview
                <span className="ml-2 font-normal text-slate-400">
                  Sample values: first_name=Bob, top message empty, sample
                  provider block in place of {'{{{provider_instructions_html}}}'}.
                </span>
              </label>
              <iframe
                title="Welcome email preview"
                srcDoc={previewSrcDoc}
                sandbox=""
                className="w-full bg-white border border-slate-200 rounded"
                style={{ height: '600px' }}
              />
            </div>
          </div>

          <div className="flex items-center gap-2 pt-2">
            <button type="submit" disabled={saving} className="btn-primary">
              {saving ? 'Saving…' : 'Save changes'}
            </button>
            {settings?.updated_at && (
              <span className="text-xs text-slate-400">
                Last edited {new Date(settings.updated_at).toLocaleString()}
              </span>
            )}
          </div>
        </form>
      )}
    </div>
  );
}
